import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useFetchViewAnswers from "../../hooks/useFetchViewAnswers";
import "./ViewAnswersScreen.css";

export default function ViewAnswersScreen() {
    const { domainId, packId, interviewId } = useParams();
    const navigate = useNavigate();

    const {
        questions = [],
        answers = {},
        evaluations = {},
        overallScore = 0,
        totalQuestions = 0,
        loading,
        error,
    } = useFetchViewAnswers(domainId, packId, interviewId);

    const [currentIndex, setCurrentIndex] = useState(0);

    // counts
    const attemptedCount = Object.keys(answers).length;
    const evaluatedCount = Object.keys(evaluations).length;
    const pendingCount = attemptedCount - evaluatedCount;

    // keyboard navigation (left/right)
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") {
                setCurrentIndex((i) => Math.min(i + 1, questions.length - 1));
            } else if (e.key === "ArrowLeft") {
                setCurrentIndex((i) => Math.max(i - 1, 0));
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [questions.length]);

    // 🚫 Disable right-click globally
    useEffect(() => {
        const handleContextMenu = (e) => e.preventDefault();
        document.addEventListener("contextmenu", handleContextMenu);
        return () => document.removeEventListener("contextmenu", handleContextMenu);
    }, []);

    if (loading) return <p className="loading-text">Loading review...</p>;
    if (error) return <p className="error-text">Error: {error}</p>;
    if (!questions || questions.length === 0) return <p className="no-questions">No answers found.</p>;

    const current = questions[currentIndex];
    const userAns = (answers[current.id]?.answer || "").trim() || "(No answer submitted)";
    const evalData = evaluations[current.id];
    const feedback = evalData?.feedback || "Evaluation pending";
    const score = evalData?.score ?? null;

    return (
        <div className="view-answers-shell">
            {/* Left navigator (desktop) */}
            <aside className="nav-left">
                <div className="nav-left-header">
                    <h3>Questions</h3>
                </div>

                <div className="nav-list">
                    {questions.map((q, idx) => {
                        const hasAns = !!answers[q.id];
                        const isEval = !!evaluations[q.id];
                        const active = idx === currentIndex;
                        return (
                            <button
                                key={q.id}
                                className={`nav-item ${active ? "active" : ""} ${isEval ? "is-eval" : hasAns ? "is-pending" : "is-empty"}`}
                                onClick={() => setCurrentIndex(idx)}
                            >
                                <div className="num">{idx + 1}</div>
                                <div className="q-short">
                                    {q.question?.slice(0, 40) + (q.question?.length > 40 ? "…" : "")}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </aside>

            {/* Center content */}
            <main className="view-main">
                <div className="view-header">
                    <div>
                        <button className="back" onClick={() => navigate(-1)}>← Back</button>
                    </div>
                    <div className="header-center">
                        <h1>Interview Review</h1>
                        <p className="sub">
                            Question {currentIndex + 1} of {totalQuestions} · Overall {overallScore}%
                        </p>
                    </div>
                    <div className="header-actions" style={{ width: "60px" }} />
                </div>

                {/* Mobile chips */}
                <div className="mobile-chips">
                    {questions.map((q, i) => {
                        const isEval = !!evaluations[q.id];
                        return (
                            <button
                                key={q.id}
                                className={`chip-mobile ${i === currentIndex ? "active" : ""} ${isEval ? "is-eval" : ""}`}
                                onClick={() => setCurrentIndex(i)}
                            >
                                {i + 1}
                            </button>
                        );
                    })}
                </div>

                <div className="view-content">
                    <article className="question-block no-copy">
                        <h2 className="question-title">Q{currentIndex + 1}.</h2>
                        {/* 🚫 No-copy question area */}
                        <pre className="question-box no-copy"><code>{current.question}</code></pre>

                        <section className="answer-block">
                            <h3>Your answer</h3>
                            <pre className="answer user"><code>{userAns}</code></pre>
                        </section>

                        <section className="evaluation-block">
                            <h3>Evaluation</h3>
                            <div className="evaluation">
                                <div className="eval-left">
                                    <div className={`score ${score === null ? "no-score" : ""}`}>
                                        {score === null ? "—" : `${score}/10`}
                                    </div>
                                </div>
                                <div className="eval-right">
                                    <div className="feedback">{feedback}</div>
                                </div>
                            </div>
                        </section>

                        {/* 🚫 Reference Answer also protected */}
                        <section className="reference-answer-block no-copy">
                            <h3>Reference Answer</h3>
                            <pre className="answer correct no-copy"><code>{current.detailedAnswer}</code></pre>
                        </section>
                    </article>
                </div>

                {/* bottom actions */}
                <div className="bottom-actions">
                    <button
                        className="btn prev"
                        onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                        disabled={currentIndex === 0}
                    >
                        Prev
                    </button>
                    <div className="spacer" />
                    <button
                        className="btn next"
                        onClick={() => setCurrentIndex(Math.min(questions.length - 1, currentIndex + 1))}
                        disabled={currentIndex === questions.length - 1}
                    >
                        Next
                    </button>
                </div>
            </main>

            {/* Right summary */}
            <aside className="summary-right">
                <div className="summary-card">
                    <h3>Summary</h3>
                    <div className="score-large">{overallScore}%</div>
                    <div className="summary-row"><span>Total</span><strong>{totalQuestions}</strong></div>
                    <div className="summary-row"><span>Attempted</span><strong>{attemptedCount}</strong></div>
                    <div className="summary-row"><span>Evaluated</span><strong>{evaluatedCount}</strong></div>
                    <div className="summary-row"><span>Pending</span><strong>{pendingCount}</strong></div>
                </div>
            </aside>
        </div>
    );
}
